import { useCallback, useMemo, useRef, useState } from "react";

/** Cuántos avisos se conservan en la campanita. */
const MAX_ITEMS = 20;

/**
 * Campanita de avisos del chat.
 *
 * Un mensaje que llega a la pestaña que el usuario está mirando no es una
 * novedad: ya lo tiene delante. Solo se anota lo que ocurre fuera de su vista
 * (el otro canal, otro hilo privado, o la pestaña del navegador en segundo
 * plano), que era justo lo que antes se perdía sin dejar rastro.
 */
export const useChatNotifications = () => {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    // Se marca en cada aviso nuevo para animar la campana una sola vez.
    const [ringing, setRinging] = useState(false);
    const ringTimer = useRef(null);

    const push = useCallback((notice) => {
        setItems((prev) => [
            { id: `${Date.now()}-${prev.length}`, seen: false, at: new Date(), ...notice },
            ...prev
        ].slice(0, MAX_ITEMS));

        setRinging(true);
        clearTimeout(ringTimer.current);
        ringTimer.current = setTimeout(() => setRinging(false), 1200);
    }, []);

    /** Da por vistos los avisos de una conversación que el usuario acaba de abrir. */
    const markSeen = useCallback((predicate) => {
        setItems((prev) => prev.map((item) => (
            !predicate || predicate(item) ? { ...item, seen: true } : item
        )));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const unread = useMemo(() => items.filter((item) => !item.seen).length, [items]);

    const toggle = useCallback(() => {
        setOpen((prev) => {
            // Abrirla es leerla: mantener el contador tras mirar la lista solo
            // confunde.
            if (!prev) markSeen();
            return !prev;
        });
    }, [markSeen]);

    return { items, unread, open, ringing, push, markSeen, clear, toggle, setOpen };
};
